import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { MatDialog as MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { ProjectsService } from '../../../../../../core/projects.service';
import { CommonService } from '../../../../../../../../shared/services/common.service';
import { ClearBidSheet, FetchActivityIdsListRequest, UploadActivityIdsFileRequest } from '../../../../../../core/projects.actions';
import { activityIdsListInfo } from '../../../../../../core/projects.selectors';
import { takeUntil } from 'rxjs/operators';
import { SuccessComponent } from '../../change-orders/success-component/success.component';
import * as $ from 'jquery';
import {
  ActivityIdsListSideNavScreenComponent,
} from '../activityIdsListSideNav/activity-ids-list-side-nav-screen/activity-ids-list-side-nav-screen.component';
import { UploadActivityIdsFileComponent } from '../upload-activity-ids-file/upload-activity-ids-file.component';
import { FormControl, FormGroup } from '@angular/forms';
import {
  activityIdsListTableSortColumnNameMap,
  activityIdsListTableSortKeyMap,
} from '../../../../../../../../shared/constants/commonConstants';

@Component({
    selector: 'app-activity-codes-list',
    templateUrl: './activity-codes-list.component.html',
    styleUrls: ['./activity-codes-list.component.scss'],
    standalone: false
})
export class ActivityCodesListComponent implements OnInit, OnDestroy {
  @ViewChild('fileUploadRef') fileUploadRef: ElementRef;
  acceptedFileTypes = [
    'application/vnd.ms-excel', // XLS
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // XLSX,
    'text/csv',
  ];
  displayedColumns: string[] = ['activityId', 'activityName', 'location', 'segment'];
  sortByColumns: string[] = ['activityId', 'activityName', 'location', 'segment'];
  dataSource: any = [];
  projectId: string;
  pagination = {
    pageIndex: 0,
    length: 0,
    pageSize: 10,
  };
  currentPage = 1;
  totalRecords: number | undefined;
  file: any;
  selectedRow: any;
  qp: any;

  form = new FormGroup({
    searchText: new FormControl(''),
  });
  itemsPerPage = 10;
  originalDataSource = [];
  selectedSortColumn: string = null;
  sortDirection = '';
  activityIdsListTableSortColumnNameMap: any;
  searchText: string;
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    public dialog: MatDialog,
    private route: ActivatedRoute,
    private store: Store,
    private projectsService: ProjectsService,
    private commonService: CommonService,
  ) {
    this.activityIdsListTableSortColumnNameMap = activityIdsListTableSortColumnNameMap;
  }

  ngOnInit() {
    this.projectId = this.route.snapshot.paramMap.get('projectId');

    this.route.queryParams.subscribe(qp => {
      this.qp = qp;

      if (qp['tab'] === 'activity-ids') {
        this.fetchActivityIdsList();
      }
    });

    this.store.select(activityIdsListInfo)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (data) {
          data = JSON.parse(JSON.stringify(data));
          this.currentPage = 1;

          if (data && data?.list?.length > 0) {
            this.originalDataSource = data?.list;
            this.totalRecords = data?.totalRecords;

            this.applyFilters();
          }
        }
      });

    this.commonService.closeDialog
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.dialog.closeAll();
        this.fetchActivityIdsList();
      });

    this.commonService.openSuccessDialog
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.openSuccessDialog();
      });

    this.commonService.clearRowSelection
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.selectedRow = null;
      });

    this.form.controls.searchText.valueChanges.pipe(
      takeUntil(this.onDestroy),
    )
      .subscribe(data => {
        if (data) {
          this.currentPage = 1;
          this.projectsService.changeCurrentPageNumber.next(this.currentPage);
          this.searchText = data;

          this.applyFilters();
        }
      });
  }

  rowClicked(data) {
    this.selectedRow = data;

    this.projectsService.openRightPanel.next({
      component: ActivityIdsListSideNavScreenComponent,
      data: {
        ...data,
        projectId: this.projectId,
      },
    });
  }

  onUploadClicked() {
    this.dialog.open(UploadActivityIdsFileComponent, {
      disableClose: false,
      data: { x: 3, projectId: this.projectId },
      panelClass: 'floating-dialog-box',
    });
  }

  openSuccessDialog() {
    this.dialog.open(SuccessComponent, {
      disableClose: false,
      data: { x: 3 },
      panelClass: 'floating-dialog-box',
    });
  }

  import(id) {
    $(`#${id}`).click();
  }

  handleFileUpload() {
    this.file = this.fileUploadRef.nativeElement.files[0];
    if (this.file) {
      if (!this.acceptedFileTypes.includes(this.file?.type)) {
        this.commonService.notification('Invalid file type.', 'danger');
        return;
      }

      const payload = {
        inputFile: this.file,
        projectId: this.projectId,
      };

      this.store.dispatch(UploadActivityIdsFileRequest({ payload }));
    }

    this.fileUploadRef.nativeElement.value = '';
  }

  onPageChanged(page: number): void {
    this.currentPage = page;
    this.pagination.pageIndex = page - 1;

    this.applyFilters();
  }

  fetchActivityIdsList() {
    const payload = {
      projectId: this.projectId,
      qp: {
        start: this.pagination.pageIndex * this.pagination.pageSize,
        total: 10000,
      },
    };

    this.store.dispatch(FetchActivityIdsListRequest({ payload }));
  }

  getTemplate() {
    const link = document.createElement('a');
    link.href = 'https://sitenotes-prod.s3.us-west-1.amazonaws.com/assets/activity_code_template.csv';
    link.download = 'activity_code_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  search() {
    const { searchText } = this.form.getRawValue();

    this.currentPage = 1;
    this.projectsService.changeCurrentPageNumber.next(this.currentPage);
    this.searchText = searchText;

    this.applyFilters();
  }

  close() {
    this.form.reset();
    this.searchText = '';
    this.fetchActivityIdsList();
  }

  onSortColumnChange(event: any): void {
    this.currentPage = 1;
    this.selectedSortColumn = event?.value;
    this.sortDirection = 'asc';
    this.projectsService.changeCurrentPageNumber.next(this.currentPage);

    this.applyFilters();
  }

  removeSort(event: Event): void {
    event?.stopPropagation();
    this.selectedSortColumn = null;
  }

  toggleSortDirection() {
    this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';

    this.applyFilters();
  }

  applyFilters() {
    let filteredData: any[];

    if (this.searchText !== undefined) {
      filteredData = this.originalDataSource.filter(obj => obj?.name?.toLowerCase().includes(this.searchText?.toLowerCase()));
    } else {
      filteredData = this.originalDataSource;
    }

    if (this.selectedSortColumn !== undefined && this.sortDirection !== undefined && filteredData.length > 0) {

      filteredData.sort((a, b) => {
        const valueA = a[activityIdsListTableSortKeyMap[this.selectedSortColumn]] ?? '';
        const valueB = b[activityIdsListTableSortKeyMap[this.selectedSortColumn]] ?? '';

        if (typeof valueA === 'number' && typeof valueB === 'number') {
          return this.sortDirection === 'asc' ? valueA - valueB : valueB - valueA;
        }

        return this.sortDirection === 'asc'
          ? String(valueA).localeCompare(String(valueB), undefined, { sensitivity: 'base' })
          : String(valueB).localeCompare(String(valueA), undefined, { sensitivity: 'base' });
      });

    }

    this.totalRecords = filteredData?.length;

    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = Math.min(startIndex + this.itemsPerPage, filteredData?.length);

    this.dataSource = filteredData.slice(startIndex, endIndex);
  }

  ngOnDestroy() {
    this.store.dispatch(ClearBidSheet());
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
